// @flow

import React from "react";
import { localize } from "redux-i18n";
import classNames from "classnames";

import { ComboBox } from "@components";
import type { OptionType } from "@components";
import type {
  AbsenceMonitoringLevel,
  AbsenceMonitoringLevelAlert,
  AbsenceMonitoringLevelMovement,
} from "@modules/service/reducer/absence-monitoring/types";

type Props = $Exact<{
  level: AbsenceMonitoringLevel,
  options: $Exact<{
    alerts: Array<AbsenceMonitoringLevelAlert>,
    levels: Array<AbsenceMonitoringLevel>,
    expiryLevels: Array<AbsenceMonitoringLevel>,
  }>,
  onChange: Function,
  disabled?: boolean,
  readonly?: boolean,
  validationErrors: ?Array<Object>,
  ...PropsFromLocalize,
}>;

export class MonitoringLevelSettings extends React.Component<Props> {
  updateAlerts = (values: Array<number>) => {
    const alerts = values.map(el => ({
      id: el,
      name: this.alertOptions.find(opt => opt.value == el)?.label || " - ",
    }));
    this.props.onChange({ ...this.props.level, alerts });
  };

  /**
   * updateMovemens(lm)(levels)
   * - lm - flag to indicate input source, either true (lm) or false (hr)
   * - levels - values emitted from either lm or hr multi select input
   *
   * The propoerty movements is used to store both lm (line manager) and hr
   * (human resources) movements, indicated by the boolean assignments to object
   * properties lm and hr.
   *
   * The data levels emitted from onChange includes all values for either lm
   * or hr.  We strip out all values of the same lm value (e.g. true, false) and
   * remap them from the emitted array data, leaving the other inputs data
   * intact.
   */
  updateMovements = (lm: boolean) => (values: Array<number>) => {
    const levels = values.map(el => ({
      id: el,
      name: this.levelsOptions.find(opt => opt.value == el)?.label || " - ",
    }));
    const currentMovements = (this.props.level || {}).movements;
    const currentFilteredByLm = currentMovements ? currentMovements.filter(m => m.lm !== lm) : [];

    const nextMovements = currentFilteredByLm.concat(
      levels.map(l => ({ level: { id: l.id }, lm, hr: !lm })),
    );

    this.props.onChange({
      ...this.props.level,
      movements: nextMovements,
    });
  };

  updateReviewPeriod = (e: SyntheticEvent<HTMLInputElement>) => {
    const val = +e.currentTarget.value || 1;
    const max = +e.currentTarget.max;
    const res = (val > 0 ? val : 1) && (val > max ? max : val);
    this.props.onChange({
      ...this.props.level,
      reviewPeriodLength: res,
    });
  };

  updateAutoExpireAfter = (e: SyntheticEvent<HTMLInputElement>) => {
    const val = +e.currentTarget.value || 1;
    const max = +e.currentTarget.max;
    const res = (val > 0 ? val : 1) && (val > max ? max : val);
    this.props.onChange({
      ...this.props.level,
      autoExpireAfter: res,
    });
  };

  updateAutoExpireActionLevel = (val: OptionType) => {
    this.props.onChange({
      ...this.props.level,
      autoExpireActionLevel: val ? { id: val } : null,
    });
  };

  get defaultAlertValues() {
    return this.props.level.alerts
      ? this.props.level.alerts
          .filter((alert: AbsenceMonitoringLevelAlert) =>
            (this.props.options.alerts || []).find(a => a.id === alert.id),
          )
          .filter((alert: AbsenceMonitoringLevelAlert) => !alert || !!alert.name)
          .map((alert: AbsenceMonitoringLevelAlert) => ({ value: alert.id, label: alert.name }))
      : [];
  }

  get alertOptions() {
    return (this.props.options.alerts || []).map((alert: AbsenceMonitoringLevelAlert) => ({
      value: alert.id,
      label: alert.name,
    }));
  }

  get levelsOptions() {
    return this.props.options.levels.map((level: AbsenceMonitoringLevel) => ({
      value: level.id,
      label: level.name,
    }));
  }

  get defaultLMValues() {
    // WTF is this
    const movements = this.props.level.movements
      ? this.props.level.movements
          .filter((move: AbsenceMonitoringLevelMovement) => move.lm)
          .map((move: AbsenceMonitoringLevelMovement) =>
            this.props.options.levels.find(l => l.id === move.level.id),
          )
          .filter((move: ?AbsenceMonitoringLevel) => !move || !!move.name)
      : null;

    return movements ? movements.map(el => (el ? { value: el.id, label: el.name } : {})) : [];
  }

  get defaultHRValues() {
    // WTF is this
    const movements = this.props.level.movements
      ? this.props.level.movements
          .filter((move: AbsenceMonitoringLevelMovement) => move.hr)
          .map((move: AbsenceMonitoringLevelMovement) =>
            this.props.options.levels.find(l => l.id === move.level.id),
          )
          .filter((move: ?AbsenceMonitoringLevel) => !move || !!move.name)
      : null;

    return movements ? movements.map(el => (el ? { value: el.id, label: el.name } : {})) : [];
  }

  get expireActionOptions() {
    return [
      {
        id: null,
        name: this.props.t("ABSENCE_MONITORING_LEVEL_AUTO_EXPIRE_ACTION_PLACEHOLDER"),
      },
      ...this.props.options.expiryLevels,
    ].map(option => ({ value: option.id, label: option.name }));
  }

  get selectedAutoExpire() {
    return this.props.level?.autoExpireActionLevel?.id
      ? this.expireActionOptions.find(
          option => option.value === this.props.level.autoExpireActionLevel?.id,
        )
      : null;
  }

  render() {
    const { level } = this.props;
    const validationErrors = this.props.validationErrors ? this.props.validationErrors : [];
    const validAlert = validationErrors.find(i => i.value === "alerts");
    const validMovements = validationErrors.find(i => i.value === "movements");

    return (
      <div className="monitoring-level-settings__container">
        <div className="monitoring-level-settings__field">
          <div className="monitoring-level-settings__field-label">
            {this.props.t("ABSENCE_MONITORING_LEVEL_ALERTS")}
          </div>
          <div className="monitoring-level-settings__field-value">
            <ComboBox
              id="ABSENCE_MONITORING_LEVEL_ALERTS_PLACEHOLDER"
              isMulti
              className={classNames("w-100", {
                readonly: this.props.readonly,
                error: validAlert,
              })}
              placeholder={this.props.t("ABSENCE_MONITORING_LEVEL_ALERTS_PLACEHOLDER")}
              options={this.alertOptions}
              value={this.defaultAlertValues}
              onChange={this.updateAlerts}
              disabled={this.props.disabled}
              readOnly={this.props.readonly}
            />
          </div>
        </div>
        <div className="monitoring-level-settings__field">
          <div className="monitoring-level-settings__field-label">
            {this.props.t("ABSENCE_MONITORING_LEVEL_LM_MOVEMENTS")}
          </div>
          <div className="monitoring-level-settings__field-value">
            <ComboBox
              id="ABSENCE_MONITORING_LEVEL_LM_MOVEMENTS_PLACEHOLDER"
              className={classNames("w-100", {
                readonly: this.props.readonly,
                error: validMovements && !(level.movements || []).find(l => l.lm === true),
              })}
              placeholder={this.props.t("ABSENCE_MONITORING_LEVEL_LM_MOVEMENTS_PLACEHOLDER")}
              isMulti
              options={this.levelsOptions}
              value={this.defaultLMValues}
              onChange={this.updateMovements(true)}
              disabled={this.props.disabled}
              readOnly={this.props.readonly}
            />
          </div>
        </div>
        <div className="monitoring-level-settings__field">
          <div className="monitoring-level-settings__field-label">
            {this.props.t("ABSENCE_MONITORING_LEVEL_HR_MOVEMENTS")}
          </div>
          <div className="monitoring-level-settings__field-value">
            <ComboBox
              id="ABSENCE_MONITORING_LEVEL_HR_MOVEMENTS_PLACEHOLDER"
              className={classNames("w-100", {
                readonly: this.props.readonly,
                error: validMovements && !(level.movements || []).find(l => l.hr === true),
              })}
              placeholder={this.props.t("ABSENCE_MONITORING_LEVEL_HR_MOVEMENTS_PLACEHOLDER")}
              isMulti
              options={this.levelsOptions}
              value={this.defaultHRValues}
              onChange={this.updateMovements(false)}
              disabled={this.props.disabled}
              readOnly={this.props.readonly}
            />
          </div>
        </div>
        <div className="monitoring-level-settings__field">
          <div className="monitoring-level-settings__field-label">
            {this.props.t("ABSENCE_MONITORING_LEVEL_REVIEW_EXPIRY")}
          </div>
          <div className="monitoring-level-settings__field-value">
            <div className="monitoring-level-settings__field-column">
              <label htmlFor="period">
                {this.props.t("ABSENCE_MONITORING_LEVEL_REVIEW_PERIOD")}
              </label>
              <div className="form-control__with-label">
                <input
                  id="period"
                  type="number"
                  className={classNames("form-control", {
                    readonly: this.props.readonly,
                    error: level.reviewPeriodLength === 0,
                  })}
                  value={this.props.level.reviewPeriodLength}
                  onChange={this.updateReviewPeriod}
                  min={1}
                  max={999}
                  disabled={this.props.disabled}
                  readOnly={this.props.readonly}
                />
                <label>months</label>
              </div>
            </div>
            <div className="monitoring-level-settings__field-column">
              <label htmlFor="expireAfter">
                {this.props.t("ABSENCE_MONITORING_LEVEL_AUTO_EXPIRE_AFTER")}
              </label>
              <div className="form-control__with-label">
                <input
                  id="expireAfter"
                  type="number"
                  className={classNames("form-control", {
                    readonly: this.props.readonly,
                  })}
                  value={this.props.level.autoExpireAfter}
                  onChange={this.updateAutoExpireAfter}
                  min={1}
                  max={999}
                  disabled={this.props.disabled}
                  readOnly={this.props.readonly}
                />
                <label>months</label>
              </div>
            </div>
            <div className="monitoring-level-settings__field-column">
              <label htmlFor="expireAction">
                {this.props.t("ABSENCE_MONITORING_LEVEL_AUTO_EXPIRE_ACTION")}
              </label>
              <ComboBox
                id="ABSENCE_MONITORING_LEVEL_AUTO_EXPIRE_ACTION"
                className={classNames("w-100", {
                  readonly: this.props.readonly,
                })}
                options={this.expireActionOptions}
                value={this.selectedAutoExpire}
                onChange={this.updateAutoExpireActionLevel}
                disabled={this.props.disabled}
                readOnly={this.props.readonly}
                withoutEmptyOption
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default localize()(MonitoringLevelSettings);
